import { Fragment } from 'react';
import type { JourneyPayload, OuterPayload, OrderTimeline } from '../../../types/analyticstypes';
import {
  formatLocalTime,
  formatSec,
  formatPrice,
  formatLatencyMs,
} from '../../../utils/analytics/format';

function OrderTimelineView({ timeline }: { timeline: OrderTimeline }) {
  const steps: { label: string; time: string | null; color: string }[] = [
    { label: 'Customer Started', time: timeline.sessionStartedAt, color: 'bg-blue-500' },
    { label: 'Order Submitted', time: timeline.orderSubmittedAt, color: 'bg-yellow-500' },
    { label: 'Kitchen Complete', time: timeline.completedAt, color: 'bg-green-500' },
  ];
  const validSteps = steps.filter((s) => s.time);
  if (validSteps.length < 2) return null;
  const durations: { label: string; sec: number }[] = [];
  const labels = ['Browse Time', 'Kitchen Time'];
  for (let i = 0; i < validSteps.length - 1; i++) {
    const from = new Date(validSteps[i].time!).getTime();
    const to = new Date(validSteps[i + 1].time!).getTime();
    const sec = (to - from) / 1000;
    if (sec > 0) durations.push({ label: labels[i] || '', sec });
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      {validSteps.map((step, i) => (
        <Fragment key={i}>
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${step.color}`} />
            <div>
              <p className="text-xs font-medium text-gray-700">{step.label}</p>
              <p className="text-xs text-gray-400 font-mono">{formatLocalTime(step.time)}</p>
            </div>
          </div>
          {i < validSteps.length - 1 && durations[i] && (
            <div className="flex items-center gap-1 px-2">
              <div className="w-8 h-px bg-gray-300" />
              <span className="text-xs text-gray-500 font-medium whitespace-nowrap">
                {formatSec(durations[i].sec)}
              </span>
              <div className="w-8 h-px bg-gray-300" />
            </div>
          )}
        </Fragment>
      ))}
    </div>
  );
}

export default function OverviewTab({
  journey,
  outer,
}: {
  journey: JourneyPayload;
  outer: OuterPayload;
}) {
  const tl = outer.timeline;
  const touchesByRoute: Record<string, number> = {};
  for (const t of journey.touchEvents) {
    touchesByRoute[t.route] = (touchesByRoute[t.route] || 0) + 1;
  }

  let payLatencyMs: number | null = null;
  let payTappedTs: number | null = null;
  for (const ev of journey.semanticEvents || []) {
    if (ev.type === 'review_pay_tapped' && typeof ev.ts === 'number') payTappedTs = ev.ts;
    else if (ev.type === 'payment_approved' && payTappedTs != null && typeof ev.ts === 'number') {
      payLatencyMs = ev.ts - payTappedTs;
      break;
    }
  }

  return (
    <div className="space-y-6">
      {tl.sessionStartedAt && (
        <div>
          <h4 className="font-semibold text-sm text-gray-700 mb-3">Order Timeline</h4>
          <OrderTimelineView timeline={tl} />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="border border-gray-200 rounded-lg p-3 bg-white">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Summary</h4>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between"><span className="text-gray-500">Items</span><span className="font-medium">{outer.itemCount}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Total</span><span className="font-medium">{formatPrice(outer.totalAmount)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Status</span><span className="font-medium">{outer.status}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Payment</span><span className="font-medium">{outer.paymentStatus}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Bag</span><span className="font-medium">{outer.bagRequested ? 'Yes' : 'No'}</span></div>
            {outer.loyaltyCustomer != null && (
              <div className="flex justify-between"><span className="text-gray-500">Loyalty customer</span><span className="font-medium">{outer.loyaltyCustomer ? 'Yes' : 'No'}</span></div>
            )}
            {outer.loyaltyEnrollAttempted != null && (
              <div className="flex justify-between"><span className="text-gray-500">Enroll attempted</span><span className="font-medium">{outer.loyaltyEnrollAttempted ? 'Yes' : 'No'}</span></div>
            )}
            {outer.feedbackRating != null && outer.feedbackMaxRating != null && (
              <div className="flex justify-between">
                <span className="text-gray-500">Customer rating</span>
                <span className="font-medium">{outer.feedbackRating}/{outer.feedbackMaxRating}</span>
              </div>
            )}
            {payLatencyMs != null && (
              <div className="flex justify-between">
                <span className="text-gray-500">Pay → Approve</span>
                <span className="font-medium font-mono">{formatLatencyMs(payLatencyMs)}</span>
              </div>
            )}
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg p-3 bg-white">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Touch Activity</h4>
          <div className="space-y-1 text-xs">
            {Object.entries(touchesByRoute)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 6)
              .map(([route, count]) => (
                <div key={route} className="flex justify-between">
                  <span className="text-gray-600 truncate">{route}</span>
                  <span className="font-medium">{count}</span>
                </div>
              ))}
            <div className="border-t pt-1 flex justify-between font-medium">
              <span>Total</span>
              <span>{journey.touchEvents.length}</span>
            </div>
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg p-3 bg-white">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Session Info</h4>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between"><span className="text-gray-500">Entry type</span><span className="font-medium">{journey.entryType || '—'}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">End reason</span><span className="font-medium">{journey.endReason}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Language</span><span className="font-medium">{journey.language}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Screen</span><span className="font-medium">{journey.screenWidth}×{journey.screenHeight}</span></div>
            {outer.createdBy && (
              <div className="flex justify-between">
                <span className="text-gray-500">Created by</span>
                <span className="font-medium truncate" title={outer.createdBy}>{outer.createdBy}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
